/**
 * A file a player picked, turned into a profile or refused.
 *
 * The gate runs outside in, and it stops at the first thing that is not what it claims:
 *
 * 1. text. A file that is not JSON is not a save, and the parser's own words say why.
 * 2. envelope. `format` says whose file this is, `formatVersion` says whether this build can read
 *    the wrapper. A wrapper from a newer build is refused without the profile being looked at.
 * 3. profile. Handed straight to `readProfile`, chain and all.
 *
 * That third step is the point of this file being short. An import is not a second way into a
 * save: it is the same gate a record off disk goes through, so migrations, the version check, name
 * sanitation, stat clamping and inventory validation are the ones already written and tested, and
 * a rule can never hold on one path and not the other.
 *
 * Nothing here trusts the file and nothing here suspects it either. A save the player edited is
 * their business; a save that would crash the game or corrupt the store is this function's.
 */

import { asNumber, asRecord, asString } from '@/save/recordFields';
import { EXPORT_FORMAT, EXPORT_FORMAT_VERSION } from '@/save/exportEnvelope';
import { readProfile } from '@/save/readProfile';
import { refused } from '@/save/saveResult';
import type { SaveMigration } from '@/save/migrationContract';
import type { SaveProfile } from '@/save/saveContract';
import type { SaveResult } from '@/save/saveResult';

export function readEnvelope(
  text: string,
  chain: readonly SaveMigration[],
): SaveResult<SaveProfile> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    return refused({ reason: 'notJson', detail: parseDetail(error) });
  }

  const envelope = asRecord(parsed, 'save');
  if (!envelope.ok) return refused({ reason: 'notASaveFile', found: describe(parsed) });

  const format = asString(envelope.value.format, 'save.format');
  if (!format.ok || format.value !== EXPORT_FORMAT) {
    return refused({ reason: 'notASaveFile', found: describe(envelope.value.format) });
  }

  const version = asNumber(envelope.value.formatVersion, 'save.formatVersion');
  if (!version.ok) return version;
  if (version.value > EXPORT_FORMAT_VERSION) {
    return refused({
      reason: 'fromNewerFormat',
      found: version.value,
      current: EXPORT_FORMAT_VERSION,
    });
  }

  return readProfile(envelope.value.profile, chain);
}

/** The parser's sentence, kept as it is: it names the position, which is the useful half. */
function parseDetail(error: unknown): string {
  return error instanceof Error ? error.message : 'the file could not be parsed';
}

/**
 * What was there instead, in one short phrase, for a message rather than for a branch.
 *
 * A string is quoted because "a file marked bob.save" reads as a fact, and an unquoted one reads
 * as this build being confused about its own name.
 */
function describe(value: unknown): string {
  if (typeof value === 'string') return `"${value}"`;
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'a list';
  return typeof value;
}
